import React, { useState } from 'react';

import { ButtonBase, Typography } from '@mui/material';
import clsx from 'clsx';
import { animated, useSpring } from 'react-spring';
import { useAlineMeasure } from 'src/hooks/dom-hooks';
import { makeStyles } from 'tss-react/mui';

const blurHeightPx = 32;
const useStyles = makeStyles()(theme => ({
  container: {
    overflowY: 'hidden',
    position: 'relative',
  },
  shadow: {
    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0), rgb(255, 255, 255))',
    backgroundSize: `100% ${blurHeightPx}px`,
    bottom: 0,
    height: blurHeightPx,
    opacity: 1,
    pointerEvents: 'none',
    position: 'absolute',
    width: '100%',
  },
  showButton: {
    marginTop: theme.spacing(1),
  },
}));

interface CollapsibleTextProps extends React.HTMLAttributes<HTMLElement> {
  maxHeightPx?: number;
}

export function CollapsibleText({
  children,
  className,
  maxHeightPx = 100,
}: CollapsibleTextProps): JSX.Element {
  const { classes } = useStyles();
  const [ref, measurements] = useAlineMeasure<HTMLDivElement>();
  const [textExpanded, setTextExpanded] = useState<boolean>(false);
  const scrollHeight = measurements?.scrollHeight ?? 0;
  const expandable = scrollHeight > maxHeightPx;
  const showBlur = expandable && !textExpanded;

  const springProps = useSpring({
    maxHeight: textExpanded ? scrollHeight : maxHeightPx,
  });

  return (
    <>
      <animated.div ref={ref} className={clsx(classes.container, className)} style={springProps}>
        {children}
        {showBlur && <div className={classes.shadow} />}
      </animated.div>
      {expandable && (
        <ButtonBase className={classes.showButton} onClick={() => setTextExpanded(!textExpanded)}>
          <Typography color="primary" variant="caption">
            {textExpanded ? 'Show less' : 'Show more'}
          </Typography>
        </ButtonBase>
      )}
    </>
  );
}
